import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PricingCard from '../components/PricingCard';

const pricingTiers = [
  {
    name: 'Free',
    price: 0,
    features: [
      '1 user',
      '3 agents',
      '100 tasks/month',
      'Community support',
      'Basic analytics',
    ],
  },
  {
    name: 'Start',
    price: 0,
    trial: true,
    features: [
      '5 users',
      '10 agents',
      '1,000 tasks/month',
      'Email support',
      'Advanced analytics',
      'Priority queuing',
    ],
  },
  {
    name: 'Growth',
    price: 99,
    isPopular: true,
    features: [
      '25 users',
      '50 agents',
      '10,000 tasks/month',
      'Priority support',
      'Custom integrations',
      'Team collaboration',
      'API access',
    ],
  },
  {
    name: 'Pro',
    price: 249,
    features: [
      '100 users',
      'Unlimited agents',
      '50,000 tasks/month',
      'Dedicated support',
      'Custom integrations',
      'White-label options',
      'SSO authentication',
      'Audit logs',
    ],
  },
  {
    name: 'Enterprise',
    price: null,
    features: [
      'Unlimited users',
      'Unlimited agents',
      'Unlimited tasks',
      'Custom SLA',
      'Dedicated CSM',
      'On-premise deployment',
      'Custom training',
      '24/7 phone support',
    ],
  },
];

const featureComparison = [
  { feature: 'Users', free: '1', start: '5', growth: '25', pro: '100', enterprise: 'Unlimited' },
  { feature: 'Agents', free: '3', start: '10', growth: '50', pro: 'Unlimited', enterprise: 'Unlimited' },
  { feature: 'Tasks/month', free: '100', start: '1,000', growth: '10,000', pro: '50,000', enterprise: 'Unlimited' },
  { feature: 'Support', free: 'Community', start: 'Email', growth: 'Priority', pro: 'Dedicated', enterprise: '24/7 Phone' },
  { feature: 'Analytics', free: 'Basic', start: 'Advanced', growth: 'Advanced', pro: 'Custom', enterprise: 'Custom' },
  { feature: 'API Access', free: '—', start: '—', growth: '✓', pro: '✓', enterprise: '✓' },
  { feature: 'SSO', free: '—', start: '—', growth: '—', pro: '✓', enterprise: '✓' },
  { feature: 'White-label', free: '—', start: '—', growth: '—', pro: '✓', enterprise: '✓' },
  { feature: 'SLA', free: '—', start: '—', growth: '—', pro: '—', enterprise: 'Custom' },
  { feature: 'Dedicated CSM', free: '—', start: '—', growth: '—', pro: '—', enterprise: '✓' },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  const handleSelectTier = (tierName: string) => {
    setSelectedTier(tierName);
    const normalized = tierName.toLowerCase();
    if (normalized === 'free' || normalized === 'start') {
      navigate('/register');
    } else {
      navigate(`/billing?plan=${normalized}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Simple, transparent pricing
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Choose the plan that fits your team. Start free, upgrade as you grow.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-16">
        {pricingTiers.map((tier) => (
          <PricingCard
            key={tier.name}
            name={tier.name}
            price={tier.price}
            features={tier.features}
            isPopular={tier.isPopular}
            isSelected={selectedTier === tier.name}
            onSelect={() => handleSelectTier(tier.name)}
            trial={tier.trial}
          />
        ))}
      </div>

      {/* Feature Comparison */}
      <div className="mt-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-8">
          Feature Comparison
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                <th className="text-left py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Feature</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Free</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Start</th>
                <th className="text-center py-4 px-6 font-semibold text-purple-600 dark:text-purple-400">Growth</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Pro</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {featureComparison.map((row, index) => (
                <tr
                  key={row.feature}
                  className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/50' : 'bg-white dark:bg-gray-800'}
                >
                  <td className="py-4 px-6 font-medium text-gray-900 dark:text-gray-100">{row.feature}</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-400">{row.free}</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-400">{row.start}</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-400 bg-purple-50 dark:bg-purple-900/20">{row.growth}</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-400">{row.pro}</td>
                  <td className="py-4 px-6 text-center text-gray-600 dark:text-gray-400">{row.enterprise}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-16 max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6">
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Can I switch plans at any time?
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately,
              and we'll prorate your next invoice.
            </p>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              What happens when I reach my task limit?
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              You'll receive a warning when you're close to your limit. Once exceeded, additional tasks
              will be queued until the next billing cycle, or you can upgrade for immediate access.
            </p>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Do you offer annual billing?
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Yes! Annual billing is available with a 20% discount. Contact our sales team for details.
            </p>
          </div>
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              What payment methods do you accept?
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              We accept all major credit cards, ACH bank transfers, and wire transfers for Enterprise plans.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
